import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Heart, Bookmark, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

export default function VibeCollection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [coll, setColl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [adopted, setAdopted] = useState(false);

  useEffect(() => {
    api.get(`/fx/collections/${id}`)
      .then(({ data }) => setColl(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const adoptAll = async () => {
    if (adopting) return;
    setAdopting(true);
    try {
      const { data } = await api.post(`/fx/collections/${id}/adopt`);
      setAdopted(true);
      toast.success(`Saved ${data.added} vibe${data.added === 1 ? "" : "s"} to your My Vibes! ✨`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save those."); }
    finally { setAdopting(false); }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;
  if (notFound || !coll) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="collection-notfound">
        <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[hsl(var(--secondary))] mx-auto mb-4"><Sparkles className="w-6 h-6 text-[hsl(var(--muted-foreground))]" /></div>
        <h1 className="font-display text-xl font-bold">This collection doesn't exist</h1>
        <Button onClick={() => navigate("/app/vibes")} className="rounded-full mt-5" data-testid="collection-gallery">Browse the gallery</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="vibe-collection">
      <button onClick={() => navigate("/app/vibes")} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Gallery</button>
      <div className="flex items-center gap-3 mb-1">
        <div className="w-14 h-14 rounded-2xl grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#a855f7] text-3xl shrink-0">{coll.emoji || "🎁"}</div>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Vibe collection</p>
          <h1 className="font-display text-xl font-extrabold leading-tight" data-testid="collection-name">{coll.name}</h1>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">by {coll.author_name} · {(coll.vibes || []).length} vibes</p>
        </div>
        <button onClick={adoptAll} disabled={adopting || adopted} data-testid="collection-adopt-all"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-60 shrink-0">
          {adopting ? <Loader2 className="w-4 h-4 animate-spin" /> : adopted ? <CheckCircle2 className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
          {adopted ? "Saved all" : "Save all to My Vibes"}
        </button>
      </div>

      {(coll.vibes || []).length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-8">This collection is empty.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {coll.vibes.map((v) => (
            <div key={v.code} role="button" tabIndex={0}
              onClick={() => navigate(`/app/vibe/${v.code}`)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/app/vibe/${v.code}`); }}
              data-testid={`collection-vibe-${v.code}`}
              className="group cursor-pointer rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden hover:border-[hsl(var(--primary))]/50 hover:-translate-y-0.5 transition-all">
              <div className="h-24 bg-gradient-to-br from-[#1f6feb] via-[#7c3aed] to-[#ec4899] grid place-items-center text-5xl relative">
                {v.emoji || "🎨"}
                <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/40 backdrop-blur px-2 py-1 text-xs font-semibold text-white"><Heart className="w-3.5 h-3.5" /> {v.likes || 0}</span>
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-bold text-sm truncate">{v.name}</h3>
                  <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] shrink-0" />
                </div>
                {Array.isArray(v.fx) && v.fx.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {v.fx.slice(0, 3).map((f, i) => <span key={i} className="text-[10px] rounded-full bg-[hsl(var(--secondary))] px-2 py-0.5 capitalize">{f}</span>)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
